import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Icon } from 'shared/components';

import * as INTERNAL_APPOINTMENT_STATUSES from 'modules/common/constants/internalAppointmentStatuses';

import { SCHEDULED } from 'modules/queues/constants/queueTypes';
import { OPENED } from 'modules/common/constants/appointmentStatuses';
import { getAppointmentStartTimeFormatted } from 'shared/utils/dateTimeUtils';
import { APPOINTMENT_STATUS_TITLES } from 'modules/queues/constants/appointmentStatusTitles';
import { CONVERSATION_STATUS_TITLES } from 'modules/queues/constants/conversationStatusTitles';
import { makeEstimationMessage } from 'modules/conference/selectors/etimatedTimeMessageFormattor';

import theme from './AppointmentDescriptionPlaceholder.scss';


const propTypes = {
  permissions: PropTypes.shape({
    canSeeStatus: PropTypes.bool.isRequired,
    canSeeSchedule: PropTypes.bool.isRequired,
    canSeeConversation: PropTypes.bool.isRequired,
  }).isRequired,
  isSIPCall: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  startTime: PropTypes.number.isRequired,
  endTime: PropTypes.number,
  status: PropTypes.string.isRequired,
  internalStatus: PropTypes.shape({
    status: PropTypes.string.isRequired,
  }).isRequired,
  queueType: PropTypes.string.isRequired,
  expectedTime: PropTypes.object,
  prerequisitesIncomplete: PropTypes.bool.isRequired,
};

const defaultProps = {
  isSIPCall: false,
  endTime: null,
  expectedTime: null,
};

const AppointmentDescriptionPlaceholder = ({
  isSIPCall,
  intl,
  startTime,
  endTime,
  internalStatus,
  status,
  permissions,
  queueType,
  expectedTime,
  prerequisitesIncomplete,
}) => {
  if (!status) return <div className={ theme.root } />;

  return (
    <div className={ theme.root }>
      <Choose>
        <When condition={
          (internalStatus.status === INTERNAL_APPOINTMENT_STATUSES.UPCOMING ||
            (internalStatus.status === INTERNAL_APPOINTMENT_STATUSES.OVERDUE && expectedTime.formattedDisplayMessage)) &&
          permissions.canSeeSchedule
        }
        >
          <Fragment>
            <div>
              <Choose>
                <When condition={ prerequisitesIncomplete }>
                  <FormattedMessage
                    id="Appointments.WaitingRoom.PrerequisitesAreRequired"
                    defaultMessage="** In order to receive a service, complete the prerequisite"
                  />
                </When>
                <When condition={ expectedTime.formattedDisplayMessage && status === OPENED }>
                  { makeEstimationMessage(expectedTime).formattedDisplayMessage }
                </When>
                <When condition={ queueType === SCHEDULED }>
                  <span style={{ whiteSpace: 'pre' }}>
                    { getAppointmentStartTimeFormatted(startTime, endTime, intl) }
                  </span>
                </When>
                <Otherwise>
                  <FormattedMessage
                    id="Appointments.WalkInIndication.WaitingInQueue"
                    defaultMessage="** Waiting in queue"
                  />
                </Otherwise>
              </Choose>
              <Icon className={ theme.icon } name="clock-outline" />
            </div>
            <If condition={ isSIPCall }>
              <div>
                <FormattedMessage
                  id="Appointment.VideoPanel.AudioOnlyNote"
                  defaultMessage="** Please note: This is an audio only consultation"
                />
              </div>
            </If>
          </Fragment>
        </When>

        <When condition={
          internalStatus.status === INTERNAL_APPOINTMENT_STATUSES.OVERDUE &&
          permissions.canSeeSchedule
        }
        >
          <div>
            <FormattedMessage
              id="Appointment.View.ConversationPanle.AppointmentOverdue"
              defaultMessage="** Appointment is overdue"
            />
            <Icon className={ theme.icon } name="clock-outline" />
          </div>
        </When>

        <When condition={
          internalStatus.status === INTERNAL_APPOINTMENT_STATUSES.HAS_LAST_CONVERSATION &&
          permissions.canSeeSchedule && permissions.canSeeConversation
        }
        >
          <Fragment>
            <div>
              <span>{ intl.formatMessage(CONVERSATION_STATUS_TITLES[internalStatus.lastConversation.status].title) }</span>
              <Icon
                className={ theme.icon }
                name={ CONVERSATION_STATUS_TITLES[internalStatus.lastConversation.status].iconName }
              />
            </div>

            <If condition={ isSIPCall }>
              <div>
                <FormattedMessage
                  id="Appointment.VideoPanel.AudioOnlyNote"
                  defaultMessage="** Please note: This is an audio only consultation"
                />
              </div>
            </If>
          </Fragment>
        </When>

        <When condition={ internalStatus.status === INTERNAL_APPOINTMENT_STATUSES.CLOSED }>
          <div className={ theme.topDescriptionPanel }>
            { permissions.canSeeStatus
              ?
                <Fragment>
                  <span>
                    { intl.formatMessage({
                      id: 'Appointment.View.ConversationPanel.ClosedAppointment.Title',
                      defaultMessage: '** Appointment closed',
                    }) }
                  </span>
                  <span>
                    { ` (${ intl.formatMessage(APPOINTMENT_STATUS_TITLES[status].title) })` }
                  </span>
                </Fragment>
              : '*****'
            }
          </div>

          <div>
            { permissions.canSeeStatus
              ?
                <Fragment>
                  <span>
                    { intl.formatMessage(APPOINTMENT_STATUS_TITLES[status].title) }
                  </span>
                  <Icon
                    className={ theme.icon }
                    name={ APPOINTMENT_STATUS_TITLES[status].iconName }
                  />
                </Fragment>
              : '*****'
            }
          </div>

        </When>
      </Choose>
    </div>
  );
};

AppointmentDescriptionPlaceholder.propTypes = propTypes;
AppointmentDescriptionPlaceholder.defaultProps = defaultProps;

export default AppointmentDescriptionPlaceholder;
